import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HandDrawnButton } from '../shared/HandDrawnButton/HandDrawnButton';
import './ContactUsMendoza.css';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  howDidYouHear: string;
  property: string;
  additionalInfo: string;
}

const ContactUsMendoza: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    country: 'USA',
    howDidYouHear: 'Instagram',
    property: 'Awasi Mendoza',
    additionalInfo: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (property: string) => {
    setFormData(prev => ({ ...prev, property }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  return (
    <div className="contact-us-section">
      {/* Background Image - Mendoza vineyard/mountain theme */}
      <div 
        className="contact-bg-image"
        style={{ backgroundImage: `url('/assets/mendoza-contact-bg.jpg')` }}
      />
      
      {/* Contact Form */}
      <div className="contact-form-wrapper">
        <div className="contact-form-header">
          <h1>{t('forms.contact.title')}</h1>
        </div>
        
        <div className="contact-form-layout">
          <form className="contact-form-inner" onSubmit={handleSubmit}>
            <div className="contact-details">
              <div className="contact-inputs">
                <div className="form-grid">
                  {/* First Name */}
                  <div className="form-field">
                    <label htmlFor="firstName">{t('forms.contact.labels.firstName')}</label>
                    <div className="field-wrapper">
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder={t('forms.contact.placeholders.firstName')}
                        required
                      />
                    </div>
                  </div>

                  {/* Last Name */}
                  <div className="form-field">
                    <label htmlFor="lastName">{t('forms.contact.labels.lastName')}</label>
                    <div className="field-wrapper">
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder={t('forms.contact.placeholders.lastName')}
                        required
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="form-field">
                    <label htmlFor="email">{t('forms.contact.labels.email')}</label>
                    <div className="field-wrapper">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder={t('forms.contact.placeholders.email')}
                        required
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className="form-field">
                    <label htmlFor="phoneNumber">{t('forms.contact.labels.phoneNumber')}</label>
                    <div className="field-wrapper">
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleInputChange}
                        placeholder={t('forms.contact.placeholders.phoneNumber')}
                      />
                    </div>
                  </div>

                  {/* Country Dropdown */}
                  <div className="form-field dropdown-field">
                    <label htmlFor="country">{t('forms.contact.labels.country')}</label>
                    <div className="field-wrapper">
                      <select
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="USA">{t('forms.contact.countries.usa')}</option>
                        <option value="UK">{t('forms.contact.countries.uk')}</option>
                        <option value="Canada">{t('forms.contact.countries.canada')}</option>
                        <option value="Australia">{t('forms.contact.countries.australia')}</option>
                        <option value="Other">{t('forms.contact.countries.other')}</option>
                      </select>
                      <img 
                        src="/assets/images/localhost/a360959ef3075e862c62c3df3826381152469c3a.svg" 
                        alt="dropdown arrow" 
                        className="dropdown-arrow"
                      />
                    </div>
                  </div>

                  {/* How did you hear Dropdown */}
                  <div className="form-field dropdown-field">
                    <label htmlFor="howDidYouHear">{t('forms.contact.labels.howDidYouHear')}</label>
                    <div className="field-wrapper">
                      <select
                        id="howDidYouHear"
                        name="howDidYouHear"
                        value={formData.howDidYouHear}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Instagram">{t('forms.contact.sources.instagram')}</option>
                        <option value="Google">{t('forms.contact.sources.google')}</option>
                        <option value="Friend">{t('forms.contact.sources.friend')}</option>
                        <option value="Press">{t('forms.contact.sources.press')}</option>
                        <option value="Other">{t('forms.contact.sources.other')}</option>
                      </select>
                      <img 
                        src="/assets/images/localhost/a360959ef3075e862c62c3df3826381152469c3a.svg" 
                        alt="dropdown arrow" 
                        className="dropdown-arrow"
                      />
                    </div>
                  </div>
                </div>

                {/* Property Selection */}
                <div className="property-selection">
                  <label className="section-label">{t('forms.contact.labels.selectProperty')}</label>
                  <div className="radio-group">
                    {[
                      { name: 'Awasi Atacama', key: 'atacama', checked: false },
                      { name: 'Awasi Patagonia', key: 'patagonia', checked: false },
                      { name: 'Awasi Iguazú', key: 'iguazu', checked: false },
                      { name: 'Awasi Santa Catarina', key: 'santaCatarina', checked: false },
                      { name: 'Awasi Mendoza', key: 'mendoza', checked: true }
                    ].map((property) => (
                      <label key={property.name} className="radio-label">
                        <img 
                          src={property.name === formData.property 
                            ? "/assets/images/localhost/98172c5e2f96b360d3c1798653599cb76ad060fd.svg"
                            : "/assets/images/localhost/291f59686e2bdde4a3709c5989acdec2d060e110.svg"
                          } 
                          alt="radio button" 
                          className="radio-icon"
                        />
                        <input
                          type="radio"
                          name="property"
                          value={property.name}
                          checked={formData.property === property.name}
                          onChange={() => handleRadioChange(property.name)}
                        />
                        <span className="radio-text">{t(`forms.contact.properties.${property.key}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="message-input">
                <label htmlFor="additionalInfo">{t('forms.contact.labels.additionalInfo')}</label>
                <div className="textarea-wrapper">
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    placeholder={t('forms.contact.placeholders.additionalInfo')}
                  />
                  <img 
                    src="/assets/images/localhost/58a66d006e1e225f4f8abbb99b831793a6c0f33b.svg" 
                    alt="underline" 
                    className="underline-svg"
                  />
                  <img 
                    src="/assets/images/localhost/922a40eeb847f0d809a144c214268fb98bbe17b0.svg" 
                    alt="corner icon" 
                    className="corner-icon"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <HandDrawnButton
              text={t('forms.contact.buttons.submit')}
              onClick={() => handleSubmit({} as React.FormEvent)}
              variant="mendoza"
              color="off-white"
              animation="fill-to-stroke"
              size="wide"
              className="contact-form-submit-button"
            />
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactUsMendoza;